import type { Network } from './spacetimedb-network';
import type { EventContext, SubscriptionHandle } from './module_bindings';
import type { GlobalCityRow } from './module_bindings/types';
import { artilleryField, parseBatteryArtillery, parseProjectileArtillery, type BatteryArtillery } from './artillery';

export interface WorldFormation {
    current: string;
    split: boolean;
    blocks: {x: number; y: number; facing: number; length: number; width: number; troops: number}[];
    shape?: {kind: 'line' | 'open_order' | 'wedge'}
        | {kind: 'hollow_square'; hole_ratio: number; fire_arc_half_angle?: number};
    own?: {
        phase: 'formed' | 'reforming' | 'rewinding' | 'disorganized' | 'dissolved';
        target: string | null; queued: string | null; waiting: string | null;
        remaining_seconds: number; exit_fraction: number; entry_fraction: number;
        retreating: boolean; commandable: boolean; can_retreat: boolean;
        can_move: boolean; can_fire: boolean; can_charge: boolean;
        modifiers: Record<string, number>;
        equipped: (Record<string, unknown> & {
            id: string; name: string; description: string; entry_seconds: number; exit_seconds: number;
            modifiers: Record<string, number>; actions: {normal_move: boolean; fire: boolean; charge: boolean};
        })[];
    };
}

export interface WorldUnit {
    id: string;
    faction_id: string;
    lon: number;
    lat: number;
    owned: boolean;
    color: string;
    frame?: WorldLocalFrame;
    tactical: Record<string, unknown> & {
        id: string; archetype: string; name: string; state: string;
        army_index: number; strength: number; max_strength: number;
        facing: number; length: number; width: number;
        marching?: boolean;
        formation?: WorldFormation | null;
        artillery?: BatteryArtillery;
        column_parent_id?: string | null;
        column_index?: number;
        column_phase?: 'none' | 'assembling' | 'marching' | 'deploying' | 'detached';
    };
}

export type WorldTerritoryCell = {x: number; y: number} & (
    {city_id: string; owner: string; color: string}
    | {city_id: null; owner: null; color: null}
);
type ClaimedTerritoryCell = Extract<WorldTerritoryCell, {city_id: string}>;

export type WorldCity = GlobalCityRow;

export interface WorldCityCapture {
    id: string;
    capturing: string | null;
    progress: number;
    contested: boolean;
}

export interface WorldFactionResources {
    gold: number;
    manpower: number;
    supplies: number;
    strategic_resources?: Record<string, number>;
}

export interface WorldEffect {
    lon: number; lat: number; metres_per_unit: number; tactical: Record<string, unknown>;
    frame?: WorldLocalFrame;
}

export interface WorldLocalFrame {
    center: {lon: number; lat: number};
    half_size: number;
}

export interface WorldBattleArea {
    id?: string;
    frame: WorldLocalFrame;
    state: Record<string, unknown>;
}

export interface WorldGroundFog {
    schema_version: 1;
    terrain_revision: string;
    observers: {x: number; y: number; range: number}[];
    terrain_sectors: {x: number; y: number}[];
    terrain_overrides: {x: number; y: number; cell_size: number; terrain: number}[];
    prefetch_margin: number;
}

export interface WorldFrame {
    type: 'frame';
    tick: number;
    faction_id: string;
    player_id: string;
    territory_faction_id: string;
    status: 'undeployed' | 'active' | 'eliminated';
    units: WorldUnit[];
    sectors: [];
    city_captures: WorldCityCapture[];
    resources?: WorldFactionResources | null;
    messengers: WorldEffect[];
    projectiles: WorldEffect[];
    engagements: Record<string, unknown>[];
    errors: string[];
    battle?: {areas: WorldBattleArea[]};
}

const record = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
const finite = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);
const geo = (value: Record<string, unknown>): boolean =>
    finite(value.lon) && Math.abs(value.lon) <= 180 && finite(value.lat) && Math.abs(value.lat) <= 85.05112878;
const localFrame = (value: unknown): value is WorldLocalFrame =>
    record(value) && record(value.center) && geo(value.center)
    && finite(value.half_size) && value.half_size > 0 && value.half_size <= 100_000;
const gridOrigin = (value: Record<string, unknown>): boolean =>
    value.origin_x === undefined && value.origin_y === undefined
    || finite(value.origin_x) && finite(value.origin_y);
const groundFog = (value: unknown): value is WorldGroundFog =>
    record(value) && value.schema_version === 1
    && typeof value.terrain_revision === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(value.terrain_revision)
    && finite(value.prefetch_margin) && value.prefetch_margin === 200
    && Array.isArray(value.observers) && value.observers.length <= 8192
    && value.observers.every(o => record(o) && finite(o.x) && finite(o.y)
        && finite(o.range) && o.range >= 0 && o.range <= 100_000)
    && Array.isArray(value.terrain_sectors) && value.terrain_sectors.length <= 65536
    && value.terrain_sectors.every(s => record(s) && Number.isInteger(s.x) && Number.isInteger(s.y)
        && finite(s.x) && s.x >= 0 && s.x < 16384 && finite(s.y) && s.y >= 0 && s.y < 16384)
    && Array.isArray(value.terrain_overrides) && value.terrain_overrides.length <= 10000
    && value.terrain_overrides.every(o => record(o) && finite(o.x) && finite(o.y)
        && finite(o.cell_size) && o.cell_size > 0 && o.cell_size <= 100_000
        && Number.isInteger(o.terrain) && finite(o.terrain) && o.terrain >= 0 && o.terrain <= 11);
const road = (value: unknown): boolean =>
    record(value) && typeof value.id === 'string'
    && (value.army_index === null || Number.isSafeInteger(value.army_index)
        && finite(value.army_index) && value.army_index >= 0)
    && (value.builder_unit_id === null || typeof value.builder_unit_id === 'string')
    && finite(value.width) && value.width > 0
    && Array.isArray(value.points) && value.points.length <= 2048
    && value.points.every(point => record(point) && finite(point.x) && finite(point.y))
    && Number.isSafeInteger(value.completed_points) && finite(value.completed_points)
    && value.completed_points >= 0 && value.completed_points <= value.points.length
    && typeof value.completed === 'boolean'
    && (!value.completed || value.completed_points === value.points.length);
const battleArea = (value: unknown): value is WorldBattleArea => {
    if (!record(value) || !localFrame(value.frame) || !record(value.state)) return false;
    if (value.id !== undefined && (typeof value.id !== 'string' || value.id.length > 1024)) return false;
    if (value.state.wood_stockpile !== undefined
        && (!finite(value.state.wood_stockpile) || value.state.wood_stockpile < 0)) return false;
    if (value.state.player_army_index !== undefined
        && (!Number.isSafeInteger(value.state.player_army_index) || !finite(value.state.player_army_index) || value.state.player_army_index < 0)) return false;
    for (const key of ['camps', 'structures', 'wall_build_projects', 'harvest_tiles', 'ant_groups', 'events']) {
        const items = value.state[key];
        if (items !== undefined && (!Array.isArray(items) || items.length > 10000 || !items.every(record))) return false;
    }
    if (value.state.roads !== undefined && (!Array.isArray(value.state.roads)
        || value.state.roads.length > 64 || !value.state.roads.every(road))) return false;
    if (value.state.follow_skipped !== undefined && (!Array.isArray(value.state.follow_skipped)
        || value.state.follow_skipped.length > 400 || !value.state.follow_skipped.every(entry =>
            record(entry) && typeof entry.unit_id === 'string' && typeof entry.reason === 'string'))) return false;
    if (Array.isArray(value.state.harvest_tiles) && !value.state.harvest_tiles.every(tile =>
        record(tile) && (tile.center_x === undefined && tile.center_y === undefined
            || finite(tile.center_x) && finite(tile.center_y)))) return false;
    const bitmap = value.state.visibility_bitmap;
    const streamed = value.state.ground_fog;
    if (streamed !== undefined && !groundFog(streamed)) return false;
    if (bitmap !== undefined && bitmap !== null) {
        if (!record(bitmap) || !gridOrigin(bitmap) || !Number.isSafeInteger(bitmap.cols) || !Number.isSafeInteger(bitmap.rows)
            || !finite(bitmap.cols) || bitmap.cols <= 0 || !finite(bitmap.rows) || bitmap.rows <= 0
            || bitmap.cols * bitmap.rows > 1_000_000 || !finite(bitmap.cell_size) || bitmap.cell_size <= 0
            || !Array.isArray(bitmap.bits) || bitmap.bits.length !== Math.ceil(bitmap.cols * bitmap.rows / 8)
            || !bitmap.bits.every(byte => Number.isInteger(byte) && byte >= 0 && byte <= 255)) return false;
    }
    const map = value.state.map;
    if (map !== undefined) {
        if (!record(map) || !gridOrigin(map)) return false;
        const {terrain_cols: cols, terrain_rows: rows, terrain_cells: cells, terrain_tiles: tiles} = map;
        if (!finite(map.cell_size) || map.cell_size <= 0
            || !Number.isSafeInteger(cols) || !Number.isSafeInteger(rows)
            || !finite(cols) || cols <= 0 || !finite(rows) || rows <= 0 || cols * rows > 1_000_000
            || (cells === undefined ? streamed === undefined
                : !Array.isArray(cells)
                    || (cells.length !== cols * rows && (cells.length !== 0 || !Array.isArray(tiles))))
            || (tiles !== undefined && (!Array.isArray(tiles) || tiles.length > 8192
                || !tiles.every(tile => record(tile) && Number.isInteger(tile.tile_x) && Number.isInteger(tile.tile_y)
                    && finite(tile.tile_x) && tile.tile_x >= 0 && tile.tile_x < cols
                    && finite(tile.tile_y) && tile.tile_y >= 0 && tile.tile_y < rows
                    && Number.isInteger(tile.terrain) && finite(tile.terrain) && tile.terrain >= 0 && tile.terrain <= 255)))) return false;
    }
    return true;
};
const battle = (value: unknown): value is {areas: WorldBattleArea[]} =>
    record(value) && Array.isArray(value.areas) && value.areas.length <= 400 && value.areas.every(battleArea);
const formationModifiers = (value: unknown): boolean => record(value)
    && ['melee_attack', 'melee_defense', 'incoming_morale_loss', 'road_speed']
        .every(key => finite(value[key]) && value[key] >= 0)
    && ['cavalry_defense', 'charge_impact', 'charge_morale_shock', 'turn_rate']
        .every(key => value[key] === undefined || finite(value[key]) && value[key] >= 0)
    && (value.turn_rate === undefined || value.turn_rate !== 0);
const formationShape = (value: unknown): boolean => record(value)
    && (['line', 'open_order', 'wedge'].includes(String(value.kind))
        && Object.keys(value).length === 1
        || value.kind === 'hollow_square'
            && Object.keys(value).every(key => ['kind', 'hole_ratio', 'fire_arc_half_angle'].includes(key))
            && finite(value.hole_ratio) && value.hole_ratio > 0 && value.hole_ratio < 1
            && (value.fire_arc_half_angle === undefined
                || finite(value.fire_arc_half_angle) && value.fire_arc_half_angle >= 0 && value.fire_arc_half_angle <= 180));
const formationActions = (value: unknown): boolean => record(value)
    && ['normal_move', 'fire', 'charge'].every(key => typeof value[key] === 'boolean');
const formation = (value: unknown, owned: boolean): boolean => {
    if (value === undefined || value === null) return true;
    if (!record(value) || typeof value.current !== 'string' || !value.current.length
        || Object.keys(value).some(key => !['current','split','blocks','shape','own'].includes(key))
        || typeof value.split !== 'boolean'
        || !Array.isArray(value.blocks) || !value.blocks.length || value.blocks.length > (value.split ? 125 : 1)
        || !value.blocks.every(block => record(block)
            && ['x', 'y', 'facing'].every(key => finite(block[key]))
            && finite(block.length) && block.length > 0 && finite(block.width) && block.width > 0
            && finite(block.troops) && Number.isInteger(block.troops) && block.troops >= 1
            && block.troops <= (value.split ? 16 : 2000)
            && (!value.split || block.length === block.width))) return false;
    if (value.shape !== undefined && (value.split || !formationShape(value.shape)
        || ['disorganized','marching_column'].includes(value.current))) return false;
    if (!owned) return value.own === undefined;
    const own = value.own;
    return record(own)
        && typeof own.phase === 'string'
        && ['formed', 'reforming', 'rewinding', 'disorganized', 'dissolved'].includes(own.phase)
        && ['target', 'queued'].every(key => own[key] === null || typeof own[key] === 'string')
        && (own.waiting === null || typeof own.waiting === 'string'
            && ['melee', 'charging', 'routing', 'recovering', 'retreating',
                'obstructed', 'restoration_blocked', 'regrouping'].includes(own.waiting))
        && finite(own.remaining_seconds) && own.remaining_seconds >= 0
        && ['exit_fraction', 'entry_fraction'].every(key => finite(own[key]) && own[key] >= 0 && own[key] <= 1)
        && ['retreating', 'commandable', 'can_retreat', 'can_move', 'can_fire', 'can_charge']
            .every(key => typeof own[key] === 'boolean')
        && formationModifiers(own.modifiers) && Array.isArray(own.equipped)
        && own.equipped.every(definition => record(definition)
            && ['id', 'name', 'description'].every(key => typeof definition[key] === 'string')
            && ['entry_seconds', 'exit_seconds'].every(key => finite(definition[key]) && definition[key] >= 0)
            && formationModifiers(definition.modifiers) && formationActions(definition.actions));
};
const unit = (value: unknown): value is WorldUnit => {
    if (!record(value) || !record(value.tactical)) return false;
    const tactical = value.tactical;
    return geo(value) && (value.frame === undefined || localFrame(value.frame)) && typeof value.id === 'string'
    && typeof value.faction_id === 'string' && typeof value.owned === 'boolean'
    && typeof value.color === 'string' && /^#[0-9a-f]{6}$/i.test(value.color)
    && typeof tactical.id === 'string'
    && formation(tactical.formation, value.owned === true)
    && tactical.id === value.id
    && ['archetype', 'name', 'state'].every(key => typeof tactical[key] === 'string')
    && (tactical.marching === undefined || typeof tactical.marching === 'boolean')
    && (tactical.column_parent_id === undefined || tactical.column_parent_id === null
        || typeof tactical.column_parent_id === 'string'
            && tactical.column_parent_id.length > 0 && tactical.column_parent_id.length <= 1024)
    && (tactical.column_index === undefined || Number.isInteger(tactical.column_index)
        && finite(tactical.column_index) && tactical.column_index >= 0 && tactical.column_index <= 0xffffffff)
    && (tactical.column_phase === undefined
        || ['none', 'assembling', 'marching', 'deploying', 'detached'].includes(String(tactical.column_phase))
            && typeof tactical.column_phase === 'string')
    && ['army_index', 'strength', 'max_strength', 'facing', 'length', 'width']
        .every(key => finite(tactical[key]));
};
const effect = (value: unknown): value is WorldEffect =>
    record(value) && geo(value) && finite(value.metres_per_unit)
    && value.metres_per_unit > 0 && value.metres_per_unit <= 16 && record(value.tactical)
    && (value.frame === undefined || localFrame(value.frame));
const territoryCoordinate = (value: unknown): value is number =>
    finite(value) && Number.isInteger(value) && value >= 0 && value < TERRITORY_GRID_SIZE;
const territoryCell = (value: unknown): value is WorldTerritoryCell =>
    record(value) && territoryCoordinate(value.x) && territoryCoordinate(value.y)
    && (value.city_id === null && value.owner === null && value.color === null
        || typeof value.city_id === 'string' && value.city_id.length > 0 && value.city_id.length <= 1024
            && typeof value.owner === 'string' && value.owner.length > 0 && value.owner.length <= 1024
            && typeof value.color === 'string' && /^#[0-9a-f]{6}$/i.test(value.color));

const cityCapture = (value: unknown): value is WorldCityCapture =>
    record(value) && typeof value.id === 'string'
    && (value.capturing === null || typeof value.capturing === 'string')
    && finite(value.progress) && value.progress >= 0 && value.progress <= 1
    && typeof value.contested === 'boolean';
const factionResources = (value: unknown): value is WorldFrame['resources'] =>
    value === undefined || value === null
    || record(value) && finite(value.gold) && finite(value.manpower) && finite(value.supplies);

function normalizeArtillery<T extends Record<string, unknown>>(value: T, projectile = false): T {
    const {artillery: raw, artilleryJson, artillery_json, ...rest} = value;
    const artillery = projectile ? parseProjectileArtillery(artilleryField(value))
        : parseBatteryArtillery(artilleryField(value));
    return {...rest, ...(artillery ? {artillery} : {})} as T;
}

export function parseWorldFrame(value: unknown): WorldFrame {
    if (!record(value) || value.type !== 'frame' || !Number.isSafeInteger(value.tick)
        || !finite(value.tick) || value.tick < 0 || typeof value.faction_id !== 'string'
        || typeof value.player_id !== 'string' || typeof value.territory_faction_id !== 'string'
        || (value.status !== 'undeployed' && value.status !== 'active' && value.status !== 'eliminated')
        || !Array.isArray(value.units) || value.units.length > 8192 || !value.units.every(unit)
        || new Set(value.units.map(unit => unit.id)).size !== value.units.length
        || new Set(value.units.map(unit => unit.tactical.column_parent_id || unit.id)).size > 400
        || !Array.isArray(value.sectors) || value.sectors.length !== 0
        || !Array.isArray(value.city_captures) || !value.city_captures.every(cityCapture)
        || !factionResources(value.resources)
        || !Array.isArray(value.messengers) || !value.messengers.every(effect)
        || !Array.isArray(value.projectiles) || !value.projectiles.every(effect)
        || !Array.isArray(value.engagements) || !value.engagements.every(record)
        || !Array.isArray(value.errors) || !value.errors.every(e => typeof e === 'string')) {
        throw new Error('The authority sent an invalid Global Campaign frame.');
    }
    let battleData: WorldFrame['battle'];
    if (value.battle !== undefined) {
        if (!battle(value.battle)) throw new Error('The authority sent an invalid Global Campaign frame (battle data).');
        battleData = value.battle;
        if ([...value.units, ...value.messengers, ...value.projectiles].some(entity => entity.frame === undefined)) {
            throw new Error('The authority sent an invalid Global Campaign frame (missing tactical source frame).');
        }
    }
    return {
        type: 'frame', tick: value.tick, faction_id: value.faction_id, status: value.status,
        player_id: value.player_id, territory_faction_id: value.territory_faction_id,
        units: value.units.map(value => ({...value, tactical: normalizeArtillery(value.tactical)})), sectors: [],
        city_captures: value.city_captures,
        ...(value.resources !== undefined ? {resources: value.resources} : {}),
        messengers: value.messengers,
        projectiles: value.projectiles.map(value => ({...value, tactical: normalizeArtillery(value.tactical, true)})),
        engagements: value.engagements, errors: value.errors,
        ...(battleData !== undefined ? {battle: battleData} : {}),
    };
}

const DEPLOYMENT_TERRAIN_LOADING = 'Terrain is loading at this location. Retry deployment when it is ready.';
const DEPLOYMENT_TERRAIN_WAIT_MS = 120_000;

const MERCATOR_WORLD = 40075016.68557849;
export const WORLD_PROTOCOL_VERSION = 5;
export const TERRITORY_GRID_SIZE = 40_076;
const TERRITORY_CELL_METRES = MERCATOR_WORLD / TERRITORY_GRID_SIZE;
const TERRITORY_BUCKET_SIZE = 64;
const TERRITORY_BUCKET_COLS = Math.ceil(TERRITORY_GRID_SIZE / TERRITORY_BUCKET_SIZE);
const CITY_GRID = 256;

export function territoryCellAt(lon: number, lat: number): {x: number; y: number} | null {
    if (!finite(lon) || !finite(lat) || Math.abs(lat) > 85.05112878) return null;
    const longitude = ((lon + 180) % 360 + 360) % 360;
    const northing = Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360));
    return {
        x: Math.min(TERRITORY_GRID_SIZE - 1, Math.floor(longitude / 360 * TERRITORY_GRID_SIZE)),
        y: Math.min(TERRITORY_GRID_SIZE - 1, Math.max(0,
            Math.floor((1 - northing / Math.PI) / 2 * TERRITORY_GRID_SIZE))),
    };
}

export function territoryCellBounds(x: number, y: number) {
    if (!territoryCoordinate(x) || !territoryCoordinate(y)) throw new Error('Invalid territory coordinates.');
    const latitude = (row: number) => Math.atan(Math.sinh(Math.PI * (1 - 2 * row / TERRITORY_GRID_SIZE))) * 180 / Math.PI;
    return {
        west: x / TERRITORY_GRID_SIZE * 360 - 180,
        east: (x + 1) / TERRITORY_GRID_SIZE * 360 - 180,
        north: latitude(y), south: latitude(y + 1),
    };
}

interface TerritorySummary {cells: number; areaKm2: number}

export class WorldTerritoryIndex {
    readonly rows = new Map<string, ClaimedTerritoryCell>();
    readonly cities = new Map<string, TerritorySummary & {owner: string; color: string}>();
    readonly factions = new Map<string, TerritorySummary>();
    private readonly buckets = new Map<number, Set<string>>();
    revision = 0;
    get size() { return this.rows.size; }

    private bucket(x: number, y: number): number {
        return Math.floor(x / TERRITORY_BUCKET_SIZE)
            + Math.floor(y / TERRITORY_BUCKET_SIZE) * TERRITORY_BUCKET_COLS;
    }

    private summarize(cell: ClaimedTerritoryCell, delta: number): void {
        const area = MERCATOR_WORLD / (2 * Math.PI) * TERRITORY_CELL_METRES / 1e6
            * (Math.tanh(Math.PI * (1 - 2 * cell.y / TERRITORY_GRID_SIZE))
                - Math.tanh(Math.PI * (1 - 2 * (cell.y + 1) / TERRITORY_GRID_SIZE)));
        const city = this.cities.get(cell.city_id) ?? {owner: cell.owner, color: cell.color, cells: 0, areaKm2: 0};
        city.cells += delta;
        city.areaKm2 += delta * area;
        if (delta > 0) { city.owner = cell.owner; city.color = cell.color; }
        if (city.cells) this.cities.set(cell.city_id, city);
        else this.cities.delete(cell.city_id);
        const faction = this.factions.get(cell.owner) ?? {cells: 0, areaKm2: 0};
        faction.cells += delta;
        faction.areaKm2 += delta * area;
        if (faction.cells) this.factions.set(cell.owner, faction);
        else this.factions.delete(cell.owner);
    }

    set(cell: WorldTerritoryCell): void {
        this.delete(cell.x, cell.y);
        if (cell.city_id === null) return;
        const key = `${cell.x}/${cell.y}`, bucket = this.bucket(cell.x, cell.y);
        this.rows.set(key, cell);
        if (!this.buckets.has(bucket)) this.buckets.set(bucket, new Set());
        this.buckets.get(bucket)!.add(key);
        this.summarize(cell, 1);
        this.revision++;
    }

    delete(x: number, y: number): void {
        const key = `${x}/${y}`, cell = this.rows.get(key);
        if (!cell) return;
        const bucket = this.bucket(x, y), entries = this.buckets.get(bucket)!;
        entries.delete(key);
        if (!entries.size) this.buckets.delete(bucket);
        this.rows.delete(key);
        this.summarize(cell, -1);
        this.revision++;
    }

    clear(): void {
        this.rows.clear();
        this.buckets.clear();
        this.cities.clear();
        this.factions.clear();
        this.revision++;
    }

    at(lon: number, lat: number): ClaimedTerritoryCell | undefined {
        const coordinate = territoryCellAt(lon, lat);
        return coordinate ? this.rows.get(`${coordinate.x}/${coordinate.y}`) : undefined;
    }

    bounds(cell: {x: number; y: number}) { return territoryCellBounds(cell.x, cell.y); }

    query(west: number, east: number, south: number, north: number): ClaimedTerritoryCell[] {
        if (![west, east, south, north].every(Number.isFinite) || east < west || north < south
            || south > MERCATOR_WORLD / 2 || north < -MERCATOR_WORLD / 2) return [];
        const n = TERRITORY_GRID_SIZE, width = TERRITORY_CELL_METRES;
        const first = Math.floor((west + MERCATOR_WORLD / 2) / width);
        const last = Math.floor((east + MERCATOR_WORLD / 2) / width);
        const x0 = ((first % n) + n) % n, x1 = ((last % n) + n) % n;
        const ranges = last - first >= n - 1 ? [[0, n - 1]]
            : Math.floor(first / n) === Math.floor(last / n) ? [[x0, x1]]
                : [[x0, n - 1], [0, x1]];
        const y0 = Math.max(0, Math.floor((MERCATOR_WORLD / 2 - north) / width));
        const y1 = Math.min(n - 1, Math.floor((MERCATOR_WORLD / 2 - south) / width));
        const result: ClaimedTerritoryCell[] = [];
        for (const [left, right] of ranges) {
            for (let by = Math.floor(y0 / TERRITORY_BUCKET_SIZE); by <= Math.floor(y1 / TERRITORY_BUCKET_SIZE); by++) {
                for (let bx = Math.floor(left / TERRITORY_BUCKET_SIZE); bx <= Math.floor(right / TERRITORY_BUCKET_SIZE); bx++) {
                    for (const key of this.buckets.get(bx + by * TERRITORY_BUCKET_COLS) ?? []) {
                        const cell = this.rows.get(key)!;
                        if (cell.x >= left && cell.x <= right && cell.y >= y0 && cell.y <= y1) result.push(cell);
                    }
                }
            }
        }
        return result;
    }
}

export class WorldCityIndex {
    readonly rows = new Map<string, WorldCity>();
    revision = 0;
    private readonly buckets = new Map<number, Set<string>>();

    private position(city: WorldCity) {
        const x = ((city.lon + 180) / 360 * MERCATOR_WORLD) % MERCATOR_WORLD;
        const y = Math.log(Math.tan(Math.PI / 4 + city.lat * Math.PI / 360)) * MERCATOR_WORLD / (2 * Math.PI);
        return {x, y};
    }

    private bucket(city: WorldCity): number {
        const {x, y} = this.position(city);
        return Math.floor(x / MERCATOR_WORLD * CITY_GRID)
            + Math.min(CITY_GRID - 1, Math.max(0, Math.floor((y / MERCATOR_WORLD + 0.5) * CITY_GRID))) * CITY_GRID;
    }

    set(city: WorldCity): void {
        this.delete(city.id);
        this.rows.set(city.id, city);
        const key = this.bucket(city);
        if (!this.buckets.has(key)) this.buckets.set(key, new Set());
        this.buckets.get(key)!.add(city.id);
        this.revision++;
    }

    delete(id: string): void {
        const previous = this.rows.get(id);
        if (!previous) return;
        const key = this.bucket(previous);
        const bucket = this.buckets.get(key)!;
        bucket.delete(id);
        if (!bucket.size) this.buckets.delete(key);
        this.rows.delete(id);
        this.revision++;
    }

    clear(): void {
        this.rows.clear();
        this.buckets.clear();
        this.revision++;
    }

    query(west: number, east: number, south: number, north: number): WorldCity[] {
        const cell = MERCATOR_WORLD / CITY_GRID;
        const firstY = Math.max(0, Math.floor((south + MERCATOR_WORLD / 2) / cell));
        const lastY = Math.min(CITY_GRID - 1, Math.floor((north + MERCATOR_WORLD / 2) / cell));
        const firstX = Math.floor((west + MERCATOR_WORLD / 2) / cell);
        const lastX = Math.min(firstX + CITY_GRID - 1, Math.floor((east + MERCATOR_WORLD / 2) / cell));
        const result: WorldCity[] = [];
        for (let y = firstY; y <= lastY; y++) {
            for (let x = firstX; x <= lastX; x++) {
                const column = ((x % CITY_GRID) + CITY_GRID) % CITY_GRID;
                for (const id of this.buckets.get(column + y * CITY_GRID) ?? []) {
                    const city = this.rows.get(id)!;
                    const p = this.position(city);
                    let mercX = p.x - MERCATOR_WORLD / 2;
                    mercX += Math.ceil((west - mercX) / MERCATOR_WORLD) * MERCATOR_WORLD;
                    if (mercX <= east && p.y >= south && p.y <= north) result.push(city);
                }
            }
        }
        return result;
    }
}

export class GlobalCampaignClient {
    frame: WorldFrame | null = null;
    revision = '';
    status = 'Loading campaigns...';
    campaignId: string | null = null;
    catalogReady = false;
    territory = new WorldTerritoryIndex();
    readonly cityMarkers = new WorldCityIndex();
    get cities() { return this.cityMarkers.rows; }
    private citySubscription: SubscriptionHandle | null = null;
    private cityConnection: Network['conn'] = null;
    private cityCampaignId: string | null = null;
    private cityEpoch = 0;
    private removeCityHandlers: (() => void) | null = null;
    private territoryRevision = -1;
    private snapshot: {revision: number; chunks: number; next: number; cells: WorldTerritoryIndex} | null = null;
    onChange: () => void = () => {};
    running = false;
    pending = false;
    deploymentStatus = '';
    private subscription: SubscriptionHandle | null = null;
    private subscribedConnection: Network['conn'] = null;
    private socket: WebSocket | null = null;
    private credential = '';
    private timer: ReturnType<typeof setInterval> | null = null;
    private reconnectAt = 0;
    private authenticated = false;
    private lastFrame = 0;
    private interest = { lon: 0, lat: 0, zoom: 2 };
    private lastInterest = '';

    constructor(private readonly network: Network) {}

    /** Local AWE campaign: one assigned army, reached on the GM's host through the Steam tunnel. */
    awe: AweSession | null = null;
    private aweArmy: AweArmyStatus | null = null;
    private readonly aweResults = new Map<string, string | null>();

    enterAwe(session: AweSession): void {
        if (this.pending) throw new Error('Wait for the pending campaign command before switching armies.');
        this.disconnect(true);
        this.stopCitySubscription();
        this.awe = session;
        this.aweArmy = null;
        this.aweResults.clear();
        this.campaignId = null;
        this.selectCampaign(session.campaignId);
    }

    leaveAwe(): void {
        this.awe = null;
        this.aweArmy = null;
        this.disconnect(true);
        this.cityMarkers.clear();
        this.campaignId = null;
    }

    private updateAweArmy(value: unknown): void {
        if (!record(value) || value.id !== this.awe?.armyId || !Number.isSafeInteger(value.sequence)) {
            throw new Error('The campaign host sent an invalid army status.');
        }
        this.aweArmy = {
            id: value.id, hasDeployed: value.has_deployed === true, eliminated: value.eliminated === true,
            pendingDeploy: value.pending_deploy === true, sequence: BigInt(value.sequence as number),
        };
    }

    get campaigns() {
        if (this.awe) {
            const awe = this.awe;
            return [{
                id: awe.campaignId, name: awe.name, ruleset: awe.ruleset, admissionOpen: true, live: true,
                formationCount: 0, capacity: 400, starterUnitCount: 0, starterSummary: '',
                endpointUrl: awe.endpoint, terrainRevision: '', tick: 0n, leaseUntil: null,
            } as any];
        }
        return this.network.conn ? Array.from(this.network.conn.db.global_world_status.iter()) : [];
    }

    get campaign() {
        return this.campaigns.find(row => row.id === this.campaignId) ?? null;
    }

    membership(campaignId: string) {
        if (this.awe) return campaignId === this.awe.campaignId ? this.aweArmy : null;
        if (!this.network.conn) return null;
        const rows = Array.from(this.network.conn.db.my_global_faction.iter());
        return rows.find(row => row.campaignId === campaignId) ?? null;
    }

    isJoinable(campaign: NonNullable<GlobalCampaignClient['campaign']>): boolean {
        return campaign.admissionOpen && campaign.live
            && campaign.capacity - campaign.formationCount >= campaign.starterUnitCount;
    }

    selectCampaign(campaignId: string): void {
        if (this.pending) throw new Error('Wait for the pending campaign command before switching campaigns.');
        if (this.campaignId === campaignId) return;
        this.disconnect(true);
        this.stopCitySubscription();
        this.campaignId = campaignId;
        this.revision = '';
        this.reconnectAt = 0;
        this.interest = {lon: 0, lat: 0, zoom: 2};
        if (this.running) this.refresh();
    }

    get ready(): boolean {
        return this.authenticated && this.socket?.readyState === WebSocket.OPEN
            && this.territoryRevision >= 0 && this.snapshot === null
            && this.lastFrame > 0 && performance.now() - this.lastFrame < 5000;
    }

    get faction() {
        return this.campaignId ? this.membership(this.campaignId) : null;
    }

    start(): void {
        this.running = true;
        this.refresh();
        if (!this.timer) this.timer = setInterval(() => this.refresh(), 500);
    }

    stop(): void {
        this.running = false;
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
        this.disconnect(true);
        this.stopCitySubscription();
        this.subscription?.unsubscribe();
        this.subscription = null;
        this.subscribedConnection = null;
    }

    private stopCitySubscription(): void {
        this.cityEpoch++;
        this.cityConnection = null;
        this.cityCampaignId = null;
        this.removeCityHandlers?.();
        this.removeCityHandlers = null;
        this.citySubscription?.unsubscribe();
        this.citySubscription = null;
        this.cityMarkers.clear();
    }

    private subscribeCities(conn: NonNullable<Network['conn']>): void {
        const campaignId = this.campaignId;
        if (this.cityConnection === conn && this.cityCampaignId === campaignId) return;
        this.stopCitySubscription();
        if (!campaignId) return;
        this.cityConnection = conn;
        this.cityCampaignId = campaignId;
        const epoch = this.cityEpoch;
        const table = conn.db.global_cities;
        const current = () => this.running && this.cityEpoch === epoch
            && this.cityConnection === conn && this.cityCampaignId === campaignId;
        let applied = false;
        let queued = false;
        const notify = () => {
            if (!applied || queued) return;
            queued = true;
            queueMicrotask(() => {
                queued = false;
                if (current()) this.onChange();
            });
        };
        const insert = (_ctx: EventContext, row: GlobalCityRow) => {
            if (!current() || row.campaignId !== campaignId) return;
            this.cityMarkers.set(row);
            notify();
        };
        const remove = (_ctx: EventContext, row: GlobalCityRow) => {
            if (!current() || row.campaignId !== campaignId) return;
            this.cityMarkers.delete(row.id);
            notify();
        };
        const update = (ctx: EventContext, oldRow: GlobalCityRow, row: GlobalCityRow) => {
            remove(ctx, oldRow);
            insert(ctx, row);
        };
        table.onInsert(insert);
        table.onUpdate(update);
        table.onDelete(remove);
        this.removeCityHandlers = () => {
            table.removeOnInsert(insert);
            table.removeOnUpdate(update);
            table.removeOnDelete(remove);
        };
        this.citySubscription = conn.subscriptionBuilder()
            .onApplied(() => {
                if (!current()) return;
                this.cityMarkers.clear();
                for (const row of table.iter()) {
                    if (row.campaignId === campaignId) this.cityMarkers.set(row);
                }
                applied = true;
                this.onChange();
            })
            .onError(context => {
                if (!current()) return;
                this.status = `City subscription failed: ${context.event?.message ?? 'unknown error'}`;
                this.stopCitySubscription();
                this.onChange();
            })
            .subscribe(`SELECT * FROM global_cities WHERE campaign_id = '${campaignId.replace(/'/g, "''")}'`);
    }

    private disconnect(clearTerritories = false): void {
        const socket = this.socket;
        this.socket = null;
        this.authenticated = false;
        this.frame = null;
        this.lastFrame = 0;
        if (clearTerritories) this.territory.clear();
        this.territoryRevision = -1;
        this.snapshot = null;
        socket?.close();
    }

    private refresh(): void {
        if (!this.running) return;
        if (this.awe) {
            this.refreshAwe();
            return;
        }
        const conn = this.network.conn;
        if (!conn || !conn.isActive) {
            this.stopCitySubscription();
            this.catalogReady = false;
            this.disconnect();
            this.status = 'Campaign backend unavailable. Reconnecting...';
            this.onChange();
            return;
        }
        if (this.subscribedConnection !== conn) {
            this.subscription?.unsubscribe();
            this.subscribedConnection = conn;
            this.subscription = conn.subscriptionBuilder()
                .onApplied(() => { this.catalogReady = true; this.refresh(); })
                .onError(context => {
                    this.status = `World subscription failed: ${context.event?.message ?? 'unknown error'}`;
                    console.error('[Global Campaign]', this.status);
                    this.catalogReady = false;
                    this.onChange();
                })
                .subscribe([
                    'SELECT * FROM global_world_status',
                    'SELECT * FROM my_global_faction',
                    'SELECT * FROM my_global_results',
                ]);
        }
        this.subscribeCities(conn);
        const world = this.campaign;
        const faction = this.campaignId
            ? Array.from(conn.db.my_global_faction.iter()).find(row => row.campaignId === this.campaignId)
            : null;
        if (!this.campaignId) {
            this.status = this.catalogReady ? 'Campaign list is up to date.' : 'Loading campaigns...';
        } else if (!world || !world.live) {
            this.disconnect();
            this.status = 'The campaign authority is not running. Reconnecting when it is available.';
        } else if (!faction) {
            this.disconnect();
            this.status = 'Join this campaign to deploy.';
        } else if (!this.socket && performance.now() >= this.reconnectAt) {
            this.open(world.endpointUrl, faction.token);
        } else if (this.socket && this.credential !== faction.token) {
            this.disconnect();
            this.open(world.endpointUrl, faction.token);
        } else if (this.lastFrame && performance.now() - this.lastFrame > 5000) {
            this.status = 'Authority updates have stopped. Orders are disabled until synchronized.';
        }
        this.maybeDeployAwe();
        this.onChange();
    }

    /** Local campaigns need no website connection: everything comes from the host. */
    private refreshAwe(): void {
        const awe = this.awe!;
        if (!this.socket && performance.now() >= this.reconnectAt) {
            this.open(awe.endpoint, '');
        } else if (this.lastFrame && performance.now() - this.lastFrame > 5000) {
            this.status = 'Host updates have stopped. Orders are disabled until the GM\'s host responds.';
        }
        this.maybeDeployAwe();
        this.onChange();
    }

    private open(endpoint: string, token: string): void {
        const url = new URL(endpoint);
        if (!['ws:', 'wss:'].includes(url.protocol)
            || (location.protocol === 'https:' && url.protocol !== 'wss:')) {
            this.status = 'The world endpoint is not compatible with this page.';
            return;
        }
        this.credential = token;
        const socket = new WebSocket(url);
        this.socket = socket;
        this.status = 'Synchronizing with the campaign authority...';
        const awe = this.awe;
        socket.onopen = () => socket.send(JSON.stringify(awe
            ? {type: 'hello', protocol_version: 1, name: awe.playerName, host_key: awe.hostKey ?? undefined, army_id: awe.armyId}
            : {type: 'hello', protocol_version: WORLD_PROTOCOL_VERSION, token}));
        socket.onmessage = event => {
            if (this.socket !== socket) return;
            try {
                const message: unknown = JSON.parse(String(event.data));
                if (!record(message)) throw new Error('Invalid world message.');
                if (awe && this.handleAweMessage(message)) {
                    this.onChange();
                    return;
                }
                if (message.type === 'welcome') {
                    if (message.protocol_version !== WORLD_PROTOCOL_VERSION || typeof message.terrain_revision !== 'string'
                        || message.world_id !== this.campaignId || message.ruleset !== this.campaign?.ruleset) {
                        throw new Error('Unsupported world protocol.');
                    }
                    this.revision = message.terrain_revision;
                    this.authenticated = true;
                    this.lastInterest = '';
                    this.setInterest(this.interest.lon, this.interest.lat, this.interest.zoom);
                } else if (message.type === 'territory_snapshot' || message.type === 'territory_update') {
                    this.applyTerritory(message);
                } else if (message.type === 'frame') {
                    if (!this.authenticated) throw new Error('The campaign authority has not authenticated.');
                    if (awe && record(message.army)) this.updateAweArmy(message.army);
                    this.frame = parseWorldFrame(message);
                    if (this.frame.faction_id !== this.faction?.id) throw new Error('Campaign faction mismatch.');
                    this.lastFrame = performance.now();
                    this.status = this.frame.status === 'eliminated'
                        ? 'Army eliminated. Choose your starting position to deploy a fresh army.'
                        : this.frame.status === 'active'
                            ? `Live world · tick ${this.frame.tick} · army remains active while offline.`
                            : 'Choose your starting position';
                    if (this.frame.errors.length) this.status += ` ${this.frame.errors.join(' ')}`;
                } else if (message.type === 'error') {
                    throw new Error(typeof message.message === 'string' ? message.message : 'World authority error.');
                } else if (message.type !== 'pong') {
                    throw new Error('Unexpected world message.');
                }
                this.onChange();
            } catch (error) {
                this.status = error instanceof Error ? error.message : String(error);
                console.error('[Global Campaign]', error);
                this.reconnectAt = performance.now() + 5000;
                this.disconnect();
                this.onChange();
            }
        };
        socket.onerror = () => {
            this.status = 'Earth authority connection failed. Your army is not removed.';
            this.onChange();
        };
        socket.onclose = () => {
            if (this.socket !== socket) return;
            this.disconnect();
            this.reconnectAt = performance.now() + 2000;
            this.status = 'Reconnecting to Earth. Your army continues under its existing orders.';
            this.onChange();
        };
    }

    /** Local-host messages that the shared world protocol does not have. Returns true when handled. */
    private handleAweMessage(message: Record<string, unknown>): boolean {
        if (message.type === 'army') {
            this.updateAweArmy(message);
        } else if (message.type === 'result') {
            if (message.army_id !== this.awe?.armyId || !Number.isSafeInteger(message.sequence)) {
                throw new Error('The campaign host sent an invalid command result.');
            }
            this.aweResults.set(String(message.sequence), typeof message.error === 'string' ? message.error : null);
        } else if (message.type === 'cities') {
            if (!Array.isArray(message.cities)) throw new Error('The campaign host sent an invalid city list.');
            this.cityMarkers.clear();
            for (const city of message.cities) {
                if (!record(city) || typeof city.id !== 'string' || !finite(city.lon) || !finite(city.lat)
                    || typeof city.campaign_id !== 'string' || typeof city.name !== 'string'
                    || typeof city.founder_id !== 'string'
                    || (city.owner !== null && typeof city.owner !== 'string')
                    || !Number.isSafeInteger(city.founded_tick) || !finite(city.founded_tick) || city.founded_tick < 0
                    || !Number.isSafeInteger(city.population) || !finite(city.population) || city.population < 0) {
                    throw new Error('The campaign host sent an invalid city record.');
                }
                this.cityMarkers.set({
                    id: city.id, campaignId: city.campaign_id, name: city.name,
                    founderId: city.founder_id, owner: typeof city.owner === 'string' ? city.owner : undefined,
                    lon: city.lon, lat: city.lat, foundedTick: BigInt(city.founded_tick), population: city.population,
                });
            }
        } else if (message.type !== 'lobby' && message.type !== 'reply') {
            return false;
        }
        return true;
    }

    private applyTerritory(message: Record<string, unknown>): void {
        if (!Number.isSafeInteger(message.revision) || !finite(message.revision) || message.revision < 0
            || !this.authenticated
            || !Array.isArray(message.cells) || message.cells.length > 512 || !message.cells.every(territoryCell)) {
            throw new Error('Invalid campaign territory message.');
        }
        if (message.type === 'territory_snapshot') {
            if (!Number.isSafeInteger(message.chunk_index) || !finite(message.chunk_index) || message.chunk_index < 0
                || !Number.isSafeInteger(message.chunk_count) || !finite(message.chunk_count)
                || message.chunk_count < 1 || message.chunk_index >= message.chunk_count) {
                throw new Error('Invalid territory snapshot chunk.');
            }
            if (message.chunk_index === 0) {
                if (this.snapshot || message.revision < this.territoryRevision) {
                    throw new Error('Territory snapshots are out of sequence. Reconnecting.');
                }
                this.snapshot = {revision: message.revision, chunks: message.chunk_count, next: 0, cells: new WorldTerritoryIndex()};
            }
            const snapshot = this.snapshot;
            if (!snapshot || snapshot.revision !== message.revision || snapshot.chunks !== message.chunk_count
                || snapshot.next !== message.chunk_index) throw new Error('Territory snapshot is incomplete. Reconnecting.');
            for (const value of message.cells) snapshot.cells.set(value);
            snapshot.next++;
            if (snapshot.next === snapshot.chunks) {
                this.territory = snapshot.cells;
                this.territoryRevision = snapshot.revision;
                this.snapshot = null;
            }
        } else {
            if (this.snapshot || this.territoryRevision < 0 || message.revision !== this.territoryRevision + 1) {
                throw new Error('Territory updates are out of sequence. Reconnecting.');
            }
            for (const value of message.cells) this.territory.set(value);
            this.territoryRevision = message.revision;
        }
    }

    setInterest(lon: number, lat: number, zoom: number): void {
        this.interest = { lon, lat, zoom };
        const key = `${lon.toFixed(3)}/${lat.toFixed(3)}/${zoom.toFixed(1)}`;
        if (this.authenticated && this.socket?.readyState === WebSocket.OPEN && key !== this.lastInterest) {
            this.socket.send(JSON.stringify({ type: 'interest', ...this.interest }));
            this.lastInterest = key;
        }
    }

    async join(): Promise<void> {
        const conn = this.network.conn;
        if (!conn?.isActive) throw new Error('Connect to the game backend and log in first.');
        if (!this.campaignId) throw new Error('Choose a campaign first.');
        const campaignId = this.campaignId;
        await conn.reducers.joinGlobalWorld({campaignId});
        const started = performance.now();
        while (!this.membership(campaignId)) {
            if (!this.running || this.network.conn !== conn || performance.now() - started > 10000) {
                throw new Error('Campaign membership has not synchronized. Refresh the campaign list and resume.');
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        this.reconnectAt = 0;
        this.refresh();
    }

    private async waitForResult(
        conn: Network['conn'], factionId: string, sequence: bigint,
    ): Promise<string | null> {
        const started = performance.now();
        while (this.running && performance.now() - started < 20000) {
            if ((!this.awe && this.network.conn !== conn) || this.faction?.id !== factionId) {
                throw new Error('World connection changed while confirming the command. Reconnect to inspect your army before retrying.');
            }
            if (this.awe) {
                const key = String(sequence);
                if (this.aweResults.has(key)) return this.aweResults.get(key) ?? null;
            } else {
                const result = Array.from(conn!.db.my_global_results.iter())
                    .find(row => row.factionId === factionId && row.sequence === sequence);
                if (result) return result.error ?? null;
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        throw new Error('Command is recorded but its result is not confirmed yet. Reconnect to inspect the army before retrying.');
    }

    private async submit(
        call: (sequence: bigint) => Promise<void>, deployment?: {lon: number; lat: number},
    ): Promise<void> {
        if (!this.ready || this.pending) throw new Error('Wait for the live authority and the previous command.');
        const faction = this.faction;
        const conn = this.network.conn;
        if (!faction || (!conn && !this.awe)) throw new Error('Join the world first.');
        const factionId = faction.id;
        const terrainDeadline = performance.now() + DEPLOYMENT_TERRAIN_WAIT_MS;
        const target = deployment ? `${deployment.lat.toFixed(4)}, ${deployment.lon.toFixed(4)}` : '';
        this.pending = true;
        this.deploymentStatus = deployment ? `Submitting deployment at ${target}...` : '';
        this.onChange();
        try {
            while (true) {
                const currentFaction = this.faction;
                const connected = this.awe ? true : !!conn?.isActive && this.network.conn === conn;
                if (!this.running || !this.ready || !connected || !currentFaction || currentFaction.id !== factionId) {
                    throw new Error('World connection changed. Reconnect before retrying the command.');
                }
                const territory = deployment && this.territory.at(deployment.lon, deployment.lat);
                if (territory && territory.owner !== this.frame?.territory_faction_id) {
                    throw new Error('Choose a location outside enemy city territory.');
                }
                if (deployment && performance.now() >= terrainDeadline) {
                    throw new Error('Terrain preparation timed out. Check the world connection and retry deployment.');
                }
                const sequence = currentFaction.sequence + 1n;
                await call(sequence);
                const error = await this.waitForResult(conn, factionId, sequence);
                if (!error) return;
                if (!deployment || error !== DEPLOYMENT_TERRAIN_LOADING) throw new Error(error);
                this.deploymentStatus = `Preparing deployment terrain at ${target}. Deployment will continue automatically...`;
                this.onChange();
                await new Promise(resolve => setTimeout(resolve,
                    Math.min(1000, Math.max(0, terrainDeadline - performance.now()))));
            }
        } finally {
            this.pending = false;
            this.deploymentStatus = '';
            this.onChange();
        }
    }

    /** Sends a world command for the active faction or, in AWE mode, the assigned army. */
    private sendCommand(sequence: bigint, command: Record<string, unknown>): Promise<void> {
        if (this.awe) return this.sendAwe({type: 'command', sequence: Number(sequence), command});
        const conn = this.network.conn;
        if (!conn || !this.campaignId) throw new Error('Campaign connection lost.');
        return conn.reducers.orderGlobalUnits({campaignId: this.campaignId, sequence, commandJson: JSON.stringify(command)});
    }

    private sendAwe(message: Record<string, unknown>): Promise<void> {
        const socket = this.socket;
        if (!socket || socket.readyState !== WebSocket.OPEN) return Promise.reject(new Error('Campaign host connection lost.'));
        socket.send(JSON.stringify(message));
        return Promise.resolve();
    }

    /** Deploys the assigned AWE army at its GM-defined location, retrying while terrain loads. */
    deployAwe(): Promise<void> {
        const awe = this.awe;
        if (!awe) return Promise.reject(new Error('No AWE army is selected.'));
        return this.submit(sequence => this.sendAwe({type: 'deploy', sequence: Number(sequence)}), awe.spawn);
    }

    private maybeDeployAwe(): void {
        const faction = this.faction;
        if (!this.awe || !faction || faction.hasDeployed || faction.pendingDeploy || !this.ready
            || this.pending || performance.now() < this.aweDeployRetryAt) return;
        this.deployAwe().catch(error => {
            this.status = error instanceof Error ? error.message : String(error);
            this.aweDeployRetryAt = performance.now() + 5000;
            this.onChange();
        });
    }

    private aweDeployRetryAt = 0;

    deployStarter(lon: number, lat: number): Promise<void> {
        if (this.awe) return Promise.reject(new Error('This campaign deploys armies from the GM setup.'));
        return this.submit(sequence => {
            const conn = this.network.conn;
            if (!conn || !this.campaignId) throw new Error('Campaign connection lost.');
            return conn.reducers.deployGlobalStarter({campaignId: this.campaignId, sequence, lon, lat});
        }, {lon, lat});
    }

    order(unitIds: string[], orderType: string, destination?: {lon: number; lat: number}, enemyUnitId?: string, stance?: string): Promise<void> {
        return this.submit(sequence => this.sendCommand(sequence, {
            action: 'order', unit_ids: unitIds, order_type: orderType,
            destination: destination ?? null, enemy_unit_id: enemyUnitId ?? null, stance: stance ?? null,
        }));
    }

    battle(commands: {action: string; payload: Record<string, unknown>}[]): Promise<void> {
        if (!commands.length || commands.length > 20) return Promise.reject(new Error('Send between 1 and 20 battle commands at a time.'));
        return this.submit(sequence => this.sendCommand(sequence, {action: 'battle', commands}));
    }

    foundCity(campId: string): Promise<void> {
        if (!campId) return Promise.reject(new Error('Choose a completed camp first.'));
        return this.submit(sequence => this.sendCommand(sequence, {action: 'found_city', camp_id: campId}));
    }
}

export interface AweSession {
    campaignId: string;
    armyId: string;
    name: string;
    ruleset: string;
    /** ws:// URL of the campaign host (its loopback port, or the Steam tunnel's local port). */
    endpoint: string;
    /** Set when this app hosts the campaign; tunnelled players are identified by Steam ID. */
    hostKey: string | null;
    playerName: string;
    /** GM-defined deployment location of the army. */
    spawn: {lon: number; lat: number};
}

/** Army status reported by a local campaign host; mirrors the `my_global_faction` fields the map uses. */
interface AweArmyStatus {
    id: string;
    hasDeployed: boolean;
    eliminated: boolean;
    pendingDeploy: boolean;
    sequence: bigint;
}
